import { useState } from "react";
import { RotateCcw, MapPin, Tag, TrendingDown, Store, AlertCircle, ChevronDown, Truck, Mail, Loader2 } from "lucide-react";

const CHAIN_COLORS = {
  Woolworths: "#178841", Coles: "#E01A22", ALDI: "#00447C", IGA: "#D71920",
  Drakes: "#0A4D8C", Foodland: "#E98300",
};
const DELIVERY_URLS = {
  Woolworths: "https://www.woolworths.com.au",
  Coles: "https://www.coles.com.au",
  ALDI: "https://www.aldi.com.au",
  IGA: "https://www.igashop.com.au",
  Drakes: "https://www.drakes.com.au",
  Foodland: "https://www.foodland.com.au",
};

const normalizeChain = (raw = "") => {
  const s = `${raw}`.toLowerCase();
  if (s.includes("wool")) return "Woolworths";
  if (s.includes("cole")) return "Coles";
  if (s.includes("aldi")) return "ALDI";
  if (s.includes("iga")) return "IGA";
  if (s.includes("drake")) return "Drakes";
  if (s.includes("foodland")) return "Foodland";
  return "";
};

export const GroceryResults = ({ result, onRestart, budgetCents, onEmailNow }) => {
  const multi = result.strategy === "MULTI_STORE";
  const none = result.strategy === "NO_STORES_AVAILABLE";
  const splits = Object.entries(result.splits || {});
  const sacrificed = result.savings_sacrificed_dollars || 0;
  const grid = result.price_grid || {};
  const chains = result.store_chains || {};

  const [emailing, setEmailing] = useState(false);
  const handleEmail = async () => {
    if (!onEmailNow) return;
    setEmailing(true);
    try { await onEmailNow(); } finally { setEmailing(false); }
  };

  // Budget analytics — compare the optimised grand total against the cap.
  const total = result.total_cost_dollars || 0;
  const budgetDollars = budgetCents ? budgetCents / 100 : 0;
  const hasBudget = budgetDollars > 0;
  const within = hasBudget && total <= budgetDollars;
  const diff = hasBudget ? Math.abs(budgetDollars - total) : 0;

  const chainFor = (storeId, name) =>
    normalizeChain(chains[storeId]) || normalizeChain(name);
  const subtotalOf = (lines) => lines.reduce((a, l) => a + (l.price_dollars || 0), 0);

  // default-expand the cheapest store (lowest subtotal); others collapsed
  const cheapestStore = splits.length
    ? splits.reduce((best, cur) => (subtotalOf(cur[1]) < subtotalOf(best[1]) ? cur : best))[0]
    : null;
  const [open, setOpen] = useState(cheapestStore ? { [cheapestStore]: true } : {});
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="flex flex-col gap-4" data-testid="sns-results">
      {/* budget status bar */}
      {hasBudget && !none && (
        <div
          data-testid="sns-budget-bar"
          data-status={within ? "under" : "over"}
          className={`rounded-2xl px-5 py-3.5 text-white ${within ? "bg-[#2E7D4F]" : "bg-[#E4572E]"}`}
        >
          {within ? (
            <p className="text-sm font-semibold leading-snug" data-testid="sns-budget-under">
              🎉 Good news! Your <span className="font-black">${total.toFixed(2)}</span> split cart is{" "}
              <span className="font-black">${diff.toFixed(2)} UNDER</span> your ${budgetDollars.toFixed(2)} weekly budget cap.
            </p>
          ) : (
            <p className="text-sm font-semibold leading-snug" data-testid="sns-budget-over">
              ⚠️ Budget Warning: this list totals <span className="font-black">${total.toFixed(2)}</span>, which exceeds
              your <span className="font-black">${budgetDollars.toFixed(2)}</span> weekly limit by{" "}
              <span className="font-black">${diff.toFixed(2)}</span>. Consider adjusting item volumes or swapping brands.
            </p>
          )}
        </div>
      )}

      {/* headline savings banner */}
      <div
        className={`rounded-3xl px-5 py-5 text-white ${multi ? "bg-[#E4572E]" : "bg-[#2E7D4F]"}`}
        data-testid="sns-strategy"
      >
        {none ? (
          <p className="text-lg font-bold">No specials found for these stores yet.</p>
        ) : multi ? (
          <>
            <div className="flex items-center gap-2 text-white/85 text-xs font-semibold uppercase tracking-wider">
              <TrendingDown className="h-4 w-4" /> Smartest split · {result.stops} stops
            </div>
            <p className="mt-1 text-3xl font-black" data-testid="sns-savings">
              You save ${result.total_savings_dollars?.toFixed(2)}
            </p>
            <p className="text-white/85 text-sm">Total basket ${result.total_cost_dollars?.toFixed(2)}</p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-white/85 text-xs font-semibold uppercase tracking-wider">
              <Store className="h-4 w-4" /> One-stop shop
            </div>
            <p className="mt-1 text-3xl font-black" data-testid="sns-savings">
              ${result.total_cost_dollars?.toFixed(2)}
            </p>
            <p className="text-white/85 text-sm">
              {sacrificed > 0
                ? `Splitting would only save $${sacrificed.toFixed(2)} — not worth the extra trip.`
                : "Cheapest in a single visit."}
            </p>
          </>
        )}
      </div>

      {/* per-store expandable checklists */}
      {splits.map(([storeId, lines]) => {
        const name = result.store_names?.[storeId] || storeId;
        const chain = chainFor(storeId, name);
        const dot = CHAIN_COLORS[chain] || "#6B7280";
        const subtotal = subtotalOf(lines);
        const isOpen = !!open[storeId];
        const deliveryUrl = DELIVERY_URLS[chain];
        return (
          <div
            key={storeId}
            data-testid={`sns-store-card-${storeId}`}
            className="rounded-3xl bg-white border border-black/5 overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.04)]"
          >
            <button
              type="button"
              onClick={() => toggle(storeId)}
              data-testid={`sns-store-toggle-${storeId}`}
              className="w-full flex items-center justify-between px-5 py-3.5 border-b border-black/5 active:bg-black/[0.02]"
            >
              <span className="flex items-center gap-2.5 font-bold text-[#2B2A26]">
                <span className="h-3 w-3 rounded-full" style={{ background: dot }} />
                <MapPin className="h-4 w-4 text-black/30" /> {name}
                <span className="text-xs font-medium text-black/40">
                  · {lines.length} item{lines.length === 1 ? "" : "s"}
                </span>
              </span>
              <span className="flex items-center gap-2.5">
                <span className="text-sm font-black text-[#2B2A26]">${subtotal.toFixed(2)}</span>
                <ChevronDown
                  className={`h-4 w-4 text-black/40 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </span>
            </button>

            {isOpen && (
              <div data-testid={`sns-store-body-${storeId}`}>
                <ul>
                  {lines.map((l, i) => {
                    const row = grid[l.product_id]?.prices || {};
                    const allStores = Object.entries(row);
                    return (
                      <li
                        key={l.product_id || i}
                        data-testid={`sns-line-${storeId}-${i}`}
                        className="px-5 py-3 border-b border-black/5 last:border-0"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[15px] text-[#2B2A26]">
                            {l.name}
                            {l.badge_text && (
                              <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-[#E4572E] bg-[#E4572E]/10 rounded-full px-2 py-0.5 align-middle">
                                <Tag className="h-3 w-3" /> {l.badge_text}
                              </span>
                            )}
                          </span>
                          <span className="text-sm font-black text-[#2E7D4F]">${l.price_dollars?.toFixed(2)}</span>
                        </div>

                        {allStores.length > 1 && (
                          <div
                            className="mt-2 flex flex-wrap gap-1.5"
                            data-testid={`sns-compare-${storeId}-${i}`}
                          >
                            {allStores.map(([sid, p]) => {
                              const c = chainFor(sid, result.store_names?.[sid] || sid);
                              const win = p.is_cheapest;
                              return (
                                <span
                                  key={sid}
                                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border ${
                                    win
                                      ? "bg-[#2E7D4F]/10 border-[#2E7D4F]/30 text-[#2E7D4F]"
                                      : "bg-black/[0.03] border-black/5 text-black/55"
                                  }`}
                                >
                                  <span
                                    className="h-2 w-2 rounded-full"
                                    style={{ background: CHAIN_COLORS[c] || "#9CA3AF" }}
                                  />
                                  {c || result.store_names?.[sid] || sid}
                                  <span className={win ? "font-black" : ""}>${p.price_dollars?.toFixed(2)}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {deliveryUrl && (
                  <div className="px-5 py-3.5">
                    <a
                      href={deliveryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`sns-delivery-${storeId}`}
                      className="flex items-center justify-center gap-2 w-full rounded-2xl py-3 text-sm font-bold text-white active:scale-[0.99] transition-transform"
                      style={{ background: dot }}
                    >
                      <Truck className="h-4 w-4" /> Online delivery · {chain || name}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {result.unmatched_items?.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid="sns-unmatched">
          <p className="flex items-center gap-2 text-xs font-bold text-amber-700 uppercase tracking-wide">
            <AlertCircle className="h-4 w-4" /> No deal found yet
          </p>
          <p className="mt-1 text-sm text-amber-800">
            {result.unmatched_items.map((u) => u.name).join(", ")}
          </p>
        </div>
      )}

      <button
        onClick={handleEmail}
        disabled={emailing}
        data-testid="sns-email-now"
        className="mt-1 w-full rounded-2xl bg-white border-2 border-[#2E7D4F] text-[#2E7D4F] font-bold py-4 text-base active:scale-[0.99] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {emailing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
        {emailing ? "Sending…" : "Email me this list"}
      </button>

      <button
        onClick={onRestart}
        data-testid="sns-restart"
        className="w-full rounded-2xl bg-[#2B2A26] text-white font-bold py-4 text-base active:scale-[0.99] transition-transform flex items-center justify-center gap-2"
      >
        <RotateCcw className="h-5 w-5" /> Start a new list
      </button>
    </div>
  );
};
